
<script lang="ts" setup>

import { reactive, onMounted, computed } from "vue";

import { useUserStore } from "@/stores/userStore";
import Notificacao from "@/components/Notificacao/Index.vue";
import * as dashboardService from "@/services/dashboardService";
import { f2dec } from "@/utils/funcoes";

const userStore = useUserStore();

var controle = reactive({
  atualizando: false,
  status: "",
  erro: "",
  dados: null as dashboardService.Dashboard | null,
})

onMounted(() => {
  carregar();
})

function carregar() {
  controle.status = "Carregando...";
  controle.erro = "";

  dashboardService.obter()
    .then((r) => {
      controle.dados = r;
      controle.status = "";
    })
    .catch((e) => {
      controle.erro = e.message || String(e);
      controle.status = "";
    });
}

const graficoPrevisao = computed(() => ({
  options: {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
    dataLabels: { enabled: false },
    colors: ["#9C9AB6", "#0c911b"],
    xaxis: { categories: controle.dados?.previsaoMensal.map(a => a.mes) ?? [] },
    yaxis: { labels: { formatter: (v: number) => "R$ " + f2dec(v) } },
    legend: { position: "top" },
    grid: { strokeDashArray: 5, borderColor: "#eee" },
    tooltip: { y: { formatter: (v: number) => "R$ " + f2dec(v) } },
  },
  series: [
    { name: "Previsto", data: controle.dados?.previsaoMensal.map(a => a.previsto) ?? [] },
    { name: "Recebido", data: controle.dados?.previsaoMensal.map(a => a.recebido) ?? [] },
  ],
}));

const graficoSituacao = computed(() => ({
  options: {
    chart: { type: "donut" },
    labels: controle.dados?.situacaoMensalidadesMes.map(a => a.nome) ?? [],
    colors: ["#06B48A", "#6560F0", "#F04D6A"],
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v: number) => "R$ " + f2dec(v) } },
  },
  series: controle.dados?.situacaoMensalidadesMes.map(a => a.valor) ?? [],
}));

const graficoNovosClientes = computed(() => ({
  options: {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "40%", borderRadius: 4 } },
    dataLabels: { enabled: true },
    colors: ["#6560F0"],
    xaxis: { categories: controle.dados?.novosClientesPorMes.map(a => a.mes) ?? [] },
    grid: { strokeDashArray: 5, borderColor: "#eee" },
  },
  series: [{ name: "Novos clientes", data: controle.dados?.novosClientesPorMes.map(a => a.qtde) ?? [] }],
}));

const graficoTopCidades = computed(() => ({
  options: {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
    dataLabels: { enabled: true, formatter: (v: number) => "R$ " + f2dec(v) },
    colors: ["#0c911b"],
    xaxis: { categories: controle.dados?.topCidades.map(a => a.nome) ?? [] },
    grid: { strokeDashArray: 5, borderColor: "#eee" },
  },
  series: [{ name: "Recebido no mês", data: controle.dados?.topCidades.map(a => a.valor) ?? [] }],
}));

</script>

<template>

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <div class="row">

    <div class="col-6 col-md-3 mb-3" v-if="userStore.hasPermissao('clientes')">
      <RouterLink to="/clientes" class="botao-home d-flex flex-column align-items-center justify-content-center text-white">
        <i class="fa fa-users"></i>
        <span>Clientes</span>
      </RouterLink>
    </div>

    <div class="col-6 col-md-3 mb-3" v-if="userStore.hasPermissao('empresas')">
      <RouterLink to="/empresas" class="botao-home d-flex flex-column align-items-center justify-content-center text-white">
        <i class="fa fa-building"></i>
        <span>Empresas</span>
      </RouterLink>
    </div>

    <div class="col-6 col-md-3 mb-3" v-if="userStore.hasPermissao('parceiros')">
      <RouterLink to="/parceiros" class="botao-home d-flex flex-column align-items-center justify-content-center text-white">
        <i class="fa fa-handshake"></i>
        <span>Parceiros</span>
      </RouterLink>
    </div>

    <div class="col-6 col-md-3 mb-3" v-if="userStore.hasPermissao('contasReceber')">
      <RouterLink to="/contasReceber" class="botao-home d-flex flex-column align-items-center justify-content-center text-white">
        <i class="fa fa-file-invoice-dollar"></i>
        <span>Contas a Receber</span>
      </RouterLink>
    </div>

  </div>

  <div class="divisoria-dashboard">
    <span>Em Construção</span>
  </div>

  <div class="text-center py-5" v-if="!controle.dados">
    <i class="fa fa-spinner fa-spin fs-2 text-muted"></i>
    <div class="text-muted mt-2">Carregando dashboard...</div>
  </div>

  <div class="row" v-if="controle.dados">

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-money-bill-wave fs-4 text-success mb-2"></i>
          <div class="fs-13 text-muted">Recebido no mês</div>
          <div class="fw-bold">R$ {{ f2dec(controle.dados.totalRecebidoMes) }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-hand-holding-usd fs-4 text-primary mb-2"></i>
          <div class="fs-13 text-muted">A receber no mês</div>
          <div class="fw-bold">R$ {{ f2dec(controle.dados.totalAReceberMes) }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-exclamation-triangle fs-4 text-danger mb-2"></i>
          <div class="fs-13 text-muted">Atrasado ({{ controle.dados.qtdeParcelasAtrasadas }})</div>
          <div class="fw-bold">R$ {{ f2dec(controle.dados.totalAtrasado) }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-file-contract fs-4 text-info mb-2"></i>
          <div class="fs-13 text-muted">Contratos ativos</div>
          <div class="fw-bold">{{ controle.dados.qtdeContratosAtivos }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-users fs-4 text-secondary mb-2"></i>
          <div class="fs-13 text-muted">Clientes ativos</div>
          <div class="fw-bold">{{ controle.dados.qtdeClientesAtivos }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-4 col-xl-2 mb-3">
      <div class="card h-100">
        <div class="card-body text-center">
          <i class="fa fa-user-plus fs-4 text-success mb-2"></i>
          <div class="fs-13 text-muted">Novos clientes no mês</div>
          <div class="fw-bold">{{ controle.dados.qtdeClientesNovosMes }}</div>
        </div>
      </div>
    </div>

  </div>

  <div class="row" v-if="controle.dados">
    <div class="col-md-8 mb-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-title fw-bold">Previsto x Recebido (últimos 6 meses)</h6>
          <apexchart type="bar" height="300" :options="graficoPrevisao.options" :series="graficoPrevisao.series"></apexchart>
        </div>
      </div>
    </div>

    <div class="col-md-4 mb-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-title fw-bold">Situação das parcelas do mês</h6>
          <apexchart type="donut" height="300" :options="graficoSituacao.options" :series="graficoSituacao.series"></apexchart>
        </div>
      </div>
    </div>
  </div>

  <div class="row" v-if="controle.dados">
    <div class="col-md-6 mb-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-title fw-bold">Novos clientes por mês</h6>
          <apexchart type="bar" height="280" :options="graficoNovosClientes.options" :series="graficoNovosClientes.series"></apexchart>
        </div>
      </div>
    </div>

    <div class="col-md-6 mb-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-title fw-bold">Top 5 cidades - recebido no mês</h6>
          <apexchart type="bar" height="280" :options="graficoTopCidades.options" :series="graficoTopCidades.series"></apexchart>
        </div>
      </div>
    </div>
  </div>


</template>

<style lang="css" scoped>

.divisoria-dashboard {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 10px 0 20px;
  color: #9C9AB6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 0.5px;
}

.divisoria-dashboard::before,
.divisoria-dashboard::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #ddd;
}

.divisoria-dashboard span {
  padding: 0 15px;
}

.botao-home {
  min-height: 140px;
  border-radius: 7px;
  background-color: #0c911b;
  text-decoration: none;
  transition: var(--transition);
  /* Sombra de elevação estilo Material Design */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2),
              0 2px 6px rgba(0, 0, 0, 0.14);
}

.botao-home i {
  font-size: 42px;
  margin-bottom: 12px;
}

.botao-home span {
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.botao-home:hover {
  background-color: #2c5293;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.22),
              0 6px 16px rgba(0, 0, 0, 0.16);
}

/* Tablet: botões menores */
@media (min-width: 768px) and (max-width: 1199.98px) {
  .botao-home {
    min-height: 100px;
  }

  .botao-home i {
    font-size: 30px;
    margin-bottom: 8px;
  }

  .botao-home span {
    font-size: 15px;
  }
}

</style>
